import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { invalidateNotificationDomains } from "@/hooks/notificationDomainsKey";
import { instanceHeaders } from "@/hooks/use-access";
import { ApiClientError, apiRequest } from "@/lib/api";
import type {
  DeliveryRow,
  EmailDeliveryView,
  InstanceTestBody,
  L1View,
  NotificationRulesView,
  PutL1Body,
  PutNotificationRulesBody,
  QuietRunRow,
  TestAccepted,
  TestStatusView,
} from "@shared/types/notifications";

/**
 * The notification module's routes (contract-notif §6.1), one hook each.
 *
 * The web never sends: both test actions write an outbox row and answer 202 with its id; the
 * runner's drain delivers it, and the screen reads the result from the status route every 2 s
 * for at most 90 s (§4.5). None of the GETs here is audited (§6.1), so polling the status of a
 * test is allowed; the rules, deliveries and card reads are never put on a timer.
 *
 * The instance-scoped reads carry the selected instance in their query key as well as in the
 * header, so switching instance can never show one instance's rules under another's name.
 */

export const NOTIFICATION_RULES_PATH = "/api/v2/instances/current/notifications";
export const NOTIFICATION_TEST_PATH = "/api/v2/instances/current/notifications/test";
export const NOTIFICATION_DELIVERIES_PATH = "/api/v2/instances/current/notifications/deliveries";
export const NOTIFICATION_QUIET_RUNS_PATH = "/api/v2/instances/current/notifications/quiet-runs";
export const EMAIL_DELIVERY_PATH = "/api/v2/platform/notifications/email";
export const EMAIL_TEST_PATH = "/api/v2/platform/notifications/email/test";
export const L1_PATH = "/api/v2/platform/notifications/l1";
export const L1_TEAMS_TEST_PATH = "/api/v2/platform/notifications/l1/teams/test";

/** The status route is read every 2 s while a test is waiting, for up to 90 s (§4.5). */
export const TEST_STATUS_POLL_MS = 2_000;
export const TEST_STATUS_GIVE_UP_MS = 90_000;
/**
 * #2158: after the give-up line a platform test's status is still read, more slowly, while its
 * dialog is open, so a delivery that ends for good later (a refused key is tried again after
 * 90 s) is said in the dialog instead of "Still waiting".
 */
export const TEST_STATUS_SLOW_POLL_MS = 10_000;

export const DELIVERIES_LIMIT = 20;

/**
 * A status read that fails for a transient reason (no answer at all, or a 5xx) is read again, twice,
 * at the poll interval, before the line says the test could not be sent (review r1 N3). A 4xx is
 * an answer and is not retried.
 */
export function retryTransientStatusRead(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  return !(error instanceof ApiClientError) || error.status === 0 || error.status >= 500;
}

export function notificationRulesKey(instanceId: string | null | undefined) {
  return [NOTIFICATION_RULES_PATH, instanceId ?? null] as const;
}

export function notificationDeliveriesKey(instanceId: string | null | undefined) {
  return [NOTIFICATION_DELIVERIES_PATH, instanceId ?? null] as const;
}

export const EMAIL_DELIVERY_QUERY_KEY = [EMAIL_DELIVERY_PATH] as const;
export const L1_QUERY_KEY = [L1_PATH] as const;

export function useNotificationRules(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<NotificationRulesView>({
    queryKey: notificationRulesKey(instanceId),
    enabled: enabled && Boolean(instanceId),
    queryFn: ({ signal }) =>
      apiRequest<NotificationRulesView>(NOTIFICATION_RULES_PATH, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

/**
 * Save one instance's rules. The request goes to the instance the caller names
 * (`instanceHeaders`, which `lib/api.ts` keeps since #449), the one the draft was read from. The
 * page still guards against writing one instance's draft into another (defence in depth): the tab
 * is keyed by the selected instance, the draft is reseeded when the view's instance changes, and
 * `handleSave` refuses when the draft's instance is not the selected one. The answer is cached
 * under the instance it names itself, so a mismatch can never file one instance's rules under
 * another's key.
 */
export function useSaveNotificationRules() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ instanceId, body }: { instanceId: string; body: PutNotificationRulesBody }) =>
      apiRequest<NotificationRulesView>(NOTIFICATION_RULES_PATH, {
        method: "PUT",
        body,
        // #1110: changing where alerts go needs a recent sign-in.
        stepUp: "change",
        headers: instanceHeaders(instanceId),
      }),
    onSuccess: (view) => {
      client.setQueryData(notificationRulesKey(view.instanceId), view);
      // #1111: the Allowed notification domains card counts saved addresses outside the list.
      invalidateNotificationDomains(client);
    },
  });
}

export function useNotificationDeliveries(enabled = true) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<DeliveryRow[]>({
    queryKey: notificationDeliveriesKey(instanceId),
    enabled: enabled && Boolean(instanceId),
    // A test that just finished, or a save, is the reason to read again; never a timer.
    staleTime: 0,
    queryFn: ({ signal }) =>
      apiRequest<DeliveryRow[]>(`${NOTIFICATION_DELIVERIES_PATH}?limit=${DELIVERIES_LIMIT}`, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

export function quietRunsKey(instanceId: string | null | undefined) {
  return [NOTIFICATION_QUIET_RUNS_PATH, instanceId ?? null] as const;
}

/**
 * Round 4 item 3: the run events that sent nothing, read only while "Runs with no alert" is
 * open (`enabled`), never on a timer.
 */
export function useQuietRuns(enabled: boolean) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<QuietRunRow[]>({
    queryKey: quietRunsKey(instanceId),
    enabled: enabled && Boolean(instanceId),
    staleTime: 0,
    queryFn: ({ signal }) =>
      apiRequest<QuietRunRow[]>(`${NOTIFICATION_QUIET_RUNS_PATH}?limit=${DELIVERIES_LIMIT}`, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

export function useSendInstanceTest() {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useMutation({
    mutationFn: (body: InstanceTestBody) =>
      apiRequest<TestAccepted>(NOTIFICATION_TEST_PATH, {
        method: "POST",
        body,
        headers: instanceHeaders(instanceId),
      }),
  });
}

/** One test's status, read every 2 s while `polling` holds. */
/** `pollMs`: every 2 s, every 10 s after the give-up line (#2158), or false. */
export function useInstanceTestStatus(eventId: string | null, pollMs: number | false) {
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id;
  return useQuery<TestStatusView>({
    queryKey: [`${NOTIFICATION_TEST_PATH}/${eventId ?? "none"}`, instanceId ?? null],
    enabled: Boolean(eventId) && Boolean(instanceId),
    staleTime: 0,
    retry: retryTransientStatusRead,
    retryDelay: TEST_STATUS_POLL_MS,
    refetchInterval: pollMs,
    queryFn: ({ signal }) =>
      apiRequest<TestStatusView>(`${NOTIFICATION_TEST_PATH}/${eventId}`, {
        signal,
        headers: instanceHeaders(instanceId),
      }),
  });
}

export function useEmailDelivery() {
  return useQuery<EmailDeliveryView>({ queryKey: EMAIL_DELIVERY_QUERY_KEY, staleTime: 0 });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: () => apiRequest<TestAccepted>(EMAIL_TEST_PATH, { method: "POST", body: {} }),
  });
}

/** A platform test's status (the test email or the L1 Teams test), read every `pollMs`. */
function usePlatformTestStatus(path: string, eventId: string | null, pollMs: number | false) {
  return useQuery<TestStatusView>({
    queryKey: [`${path}/${eventId ?? "none"}`],
    enabled: Boolean(eventId),
    staleTime: 0,
    retry: retryTransientStatusRead,
    retryDelay: TEST_STATUS_POLL_MS,
    refetchInterval: pollMs,
  });
}

export function useEmailTestStatus(eventId: string | null, pollMs: number | false) {
  return usePlatformTestStatus(EMAIL_TEST_PATH, eventId, pollMs);
}

/** #2165: "Send a test" on the L1 Teams channel card. */
export function useSendL1TeamsTest() {
  return useMutation({
    mutationFn: () => apiRequest<TestAccepted>(L1_TEAMS_TEST_PATH, { method: "POST", body: {} }),
  });
}

export function useL1TeamsTestStatus(eventId: string | null, pollMs: number | false) {
  return usePlatformTestStatus(L1_TEAMS_TEST_PATH, eventId, pollMs);
}

export function useL1Recipients() {
  return useQuery<L1View>({ queryKey: L1_QUERY_KEY });
}

export function useSaveL1Recipients() {
  const client = useQueryClient();
  return useMutation({
    // #1110: where the L1 alerts go needs a recent sign-in.
    mutationFn: (body: PutL1Body) =>
      apiRequest<L1View>(L1_PATH, { method: "PUT", body, stepUp: "change" }),
    onSuccess: (view) => {
      client.setQueryData(L1_QUERY_KEY, view);
    },
  });
}
